import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class HtmlDimensionService {

  /**
   * Parses a dimension (width or height) from HTML attribute or inline style
   * on the root element of the block HTML string.
   */
  private parseAttributeDimension(html: string, attr: 'width' | 'height'): number | null {
    const attrMatch = html.match(new RegExp(`${attr}="(\\d+)"`, 'i'));
    if (attrMatch) return parseInt(attrMatch[1]);

    const styleMatch = html.match(new RegExp(`${attr}\\s*:\\s*(\\d+)px`, 'i'));
    if (styleMatch) return parseInt(styleMatch[1]);

    return null;
  }

  /**
   * Returns the appropriate screenshot width for the given block type:
   * - STRIPE     → 1000 (fixed Stripo convention)
   * - STRUCTURE  → 600 (standard email body width)
   * - CONTAINER  → parsed from blockHtml width attribute, fallback 600
   */
  getBlockWidth(blockHtml: string, blockType: string): number {
    if (blockType === 'STRIPE') return 1000;
    if (blockType === 'CONTAINER') {
      return this.parseAttributeDimension(blockHtml, 'width') ?? 600;
    }
    // STRUCTURE — standard email width
    return 600;
  }

  /**
   * Builds a valid XHTML email document wrapping blockHtml in the proper
   * <table> hierarchy that Stripo's screenshot API expects.
   * A bare <td> in <body> is invalid and causes white/broken screenshots.
   */
  buildScreenshotHtml(blockHtml: string, css: string, width: number, blockType: string): string {
    const head = `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
<style>
${css}
</style>
</head>`;

    if (blockType === 'STRIPE') {
      // STRIPE blocks already contain full-width tables with align="center".
      // A simple body wrapper preserves centering without introducing a
      // constraining outer table.
      return `${head}
<body style="margin:0;padding:0;">
${blockHtml}
</body>
</html>`;
    }

    // STRUCTURE / CONTAINER: the blockHtml is a bare <td>, which needs a
    // valid <table><tr> parent and a fixed-width outer wrapper to render correctly.
    return `${head}
<body class="body" style="margin:0;padding:0;">
<div class="es-wrapper-color">
<table cellpadding="0" cellspacing="0" width="${width}" class="es-wrapper" style="width:${width}px;">
<tbody><tr><td valign="top">
<table class="es-content" cellpadding="0" cellspacing="0" align="center" style="width:${width}px;">
<tbody><tr>
${blockHtml}
</tr></tbody>
</table>
</td></tr></tbody>
</table>
</div>
</body>
</html>`;
  }

  /**
   * Measures the rendered height of screenshotHtml inside a hidden iframe
   * sized to the given width. Returns { width, height }.
   */
  async calculateDimensions(screenshotHtml: string, width: number): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = `position:absolute;visibility:hidden;width:${width}px;height:0;border:none;`;

      document.body.appendChild(iframe);

      const cleanup = () => {
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
      };

      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) {
          cleanup();
          reject(new Error('Cannot access iframe document'));
          return;
        }

        iframeDoc.open();
        iframeDoc.write(screenshotHtml);
        iframeDoc.close();

        iframe.onload = () => {
          setTimeout(() => {
            try {
              const body = iframeDoc.body;
              const html = iframeDoc.documentElement;

              const firstChild = body?.firstElementChild as HTMLElement | null;
              const height = firstChild
                ? Math.ceil(firstChild.getBoundingClientRect().height)
                : Math.max(
                    body?.scrollHeight || 0,
                    body?.offsetHeight || 0,
                    html?.scrollHeight || 0,
                    html?.offsetHeight || 0
                  );

              resolve({ width, height });
            } catch (error) {
              reject(error);
            } finally {
              cleanup();
            }
          }, 150);
        };

        iframe.onerror = () => {
          cleanup();
          reject(new Error('Failed to load iframe content'));
        };

      } catch (error) {
        cleanup();
        reject(error);
      }
    });
  }

  /**
   * Synchronous fallback that estimates dimensions from HTML string.
   * Use only when async calculation isn't possible.
   */
  estimateDimensions(html: string, _css: string): { width: number; height: number } {
    const widthMatch = html.match(/width="(\d+)"/);
    const width = widthMatch ? parseInt(widthMatch[1]) : 700;

    const imageCount = (html.match(/<img/g) || []).length;
    const textLength = html.replace(/<[^>]*>/g, '').length;
    const estimatedHeight = Math.max(
      300,
      imageCount * 400 + Math.ceil(textLength / 100) * 20
    );

    return { width, height: estimatedHeight };
  }
}
